import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { toastr } from '@/utils/toast'
import axios from 'axios'
import countries from '@/lib/countries'

import config from '@/config/config-esm'
import { setFormData } from '@/store/slices/formDataSlice'
import { createBoundLookupPerson } from '@/lib/person_lookup'
import { randStr } from '@/lib/utils'
import PortType from '@/components/PortType'
import DateOfBirthPicker from '@/components/DateOfBirthPicker'

const SMS_POLL_TIMEOUT = 1000

interface OwnerInputProps {
  person: any
  mode: 'single' | 'family'
  handleChange: (e: any, resetVerification?: boolean) => void
  hidePortType?: boolean
  ownerWantsSubscription: boolean
  onOwnerWantsSubscriptionChange: (value: boolean) => void
  onPersonUpdate: (person: any) => void
  lookupPersonFn?: any
}

const OwnerInputComponent: React.FC<OwnerInputProps> = ({
  person,
  mode,
  handleChange,
  hidePortType,
  ownerWantsSubscription,
  onOwnerWantsSubscriptionChange,
  onPersonUpdate,
  lookupPersonFn,
}) => {
  const dispatch = useDispatch()
  const user = useSelector((state: any) => state.user)
  const appData = useSelector((state: any) => state.appData)
  const formData = useSelector((state: any) => state.formData)
  const layout = useSelector((state: any) => state.layout)

  const [doingSmsLookup, setDoingSmsLookup] = useState(false)
  const [missingAddress, setMissingAddress] = useState('')
  const [missingPostalCode, setMissingPostalCode] = useState('')
  const [missingPostalArea, setMissingPostalArea] = useState('')
  const [clientId, setClientId] = useState<string | null>(null)
  const [serverId, setServerId] = useState<string | null>(null)

  const checkSMSTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Create context for person lookup
  const lookupContext = {
    dispatch,
    formData,
    onPersonUpdate,
    capitalizeFirstLetter: (str: string) => {
      str = str.toLowerCase()
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  }

  // Create bound lookup function
  const boundLookupPerson = lookupPersonFn || createBoundLookupPerson(lookupContext)

  useEffect(() => {
    // Reset deferred flag when component is mounted for a new sale
    dispatch(setFormData({ personLookupDeferred: false }))

    return () => {
      if (checkSMSTimeoutRef.current) {
        clearTimeout(checkSMSTimeoutRef.current)
      }
    }
  }, [dispatch])

  const initiateSMSVerification = () => {
    const subscription = appData.subscriptions.find((s: any) => s.id === person.subscriptionId)
    const newClientId = randStr(8)
    setClientId(newClientId)

    const data = {
      phone: person.ownerPhone,
      client_id: newClientId,
      type: 'single_owner',
      monthly_cost: subscription?.amount,
      package_name: subscription ? subscription.name : null,
    }

    setDoingSmsLookup(true)

    axios
      .post(`${config.newApiUrl}/rpc/initiate_sms_verification`, data)
      .then((r) => {
        console.log('reply from server', r)
        if (r.data.status === 'ok') {
          setServerId(r.data.server_id)
          checkSMSTimeoutRef.current = setTimeout(checkSMSStatus, SMS_POLL_TIMEOUT)
        }
      })
      .catch((e) => {
        toastr.error(
          'Kunne ikke starte SMS-verifisering',
          `Vennligst prøv på nytt, eller kontakt...`,
        )
        setDoingSmsLookup(false)
      })
  }

  const checkSMSStatus = () => {
    if (checkSMSTimeoutRef.current) {
      clearTimeout(checkSMSTimeoutRef.current)
    }

    const data = {
      phone: person.ownerPhone,
      client_id: clientId,
      server_id: serverId,
    }

    axios
      .post(`${config.newApiUrl}/rpc/check_sms_verification`, data)
      .then((r) => {
        console.log('reply from server', r)
        if (r.data.status === 'ok') {
          if (checkSMSTimeoutRef.current) {
            clearTimeout(checkSMSTimeoutRef.current)
          }
          setDoingSmsLookup(false)
          dispatch(setFormData({ singleExistingOwnerPhoneNo: person.ownerPhone }))

          onPersonUpdate({
            ...person,
            verified: true,
          })
        } else if (r.data.status === 'pending') {
          checkSMSTimeoutRef.current = setTimeout(checkSMSStatus, SMS_POLL_TIMEOUT)
        }
      })
      .catch((e) => {
        console.log('error checking SMS status', e)
        checkSMSTimeoutRef.current = setTimeout(checkSMSStatus, 5000)
      })
  }

  const doSmsPersonLookup = () => {
    initiateSMSVerification()
  }

  const updatePersonData = () => {
    if (
      (person.address || missingAddress) &&
      (person.postalCode || missingPostalCode) &&
      (person.postalArea || missingPostalArea)
    ) {
      onPersonUpdate({
        ...person,
        verified: true,
        partlyVerified: false,
        address: person.address || missingAddress,
        postalCode: person.postalCode || missingPostalCode,
        postalArea: person.postalArea || missingPostalArea,
      })
      // If DSF lookup failed and user is updating manually, mark sale as deferred
      // dispatch(setFormData({ personLookupDeferred: true }))
    }
  }

  const doFakePersonLookup = () => {
    onPersonUpdate({
      ...person,
      // verified: true,
      verified: false,
      partlyVerified: true,
      // address: 'Fjellveien 18 B',
      postalCode: '3121',
      postalArea: 'Nøtterøy',
      dateOfBirth: '1974-09-10',
    })
  }

  const doPersonLookup = (fakeError?: boolean) => {
    const { lookupMethod } = formData
    boundLookupPerson(lookupMethod, person, mode, fakeError)
  }

  const setLookupMethod = (lookupMethod: string) => {
    dispatch(setFormData({ lookupMethod }))
  }

  const showPersonalia = () => {
    return (
      (mode === 'single' && (ownerWantsSubscription || person.verified)) ||
      (mode === 'family' && ownerWantsSubscription)
    )
  }

  const { lookupMethod } = formData
  const { departmentId } = layout.data
  const isUkraineFlow = departmentId === config.ukraineDepartmentId
  const familyExtraServices = appData.familyExtraServices || []
  const filteredFamilyExtraServices = config.campaignsAsExtraServices
    .map((c: any) => {
      return { id: c[1], name: c[0], active: true }
    })
    .concat(familyExtraServices)
    .filter((s: any) => s.active && s.id !== config.digitalSafetyId)

  return (
    <React.Fragment>
      {!isUkraineFlow && (
        <div className="input_wrapper more-spacing">
          <div className="input_wrapper">
            <input
              type="radio"
              value={true}
              id="owner-wants-sub"
              checked={ownerWantsSubscription}
              onClick={(e) => {
                onOwnerWantsSubscriptionChange(true)
                onPersonUpdate({
                  ...person,
                  extraAbroad: ownerWantsSubscription ? person.extraAbroad : false,
                  extraAbroadId: ownerWantsSubscription ? person.extraAbroadId : null,
                })
              }}
              onChange={(e) => {}}
            />
            <label htmlFor="owner-wants-sub">
              <span>
                {mode === 'family' && 'Eier skal kjøpe abonnement til seg selv også'}
                {mode === 'single' && 'Eier kjøper abonnement til seg selv'}
              </span>
            </label>
          </div>
          <div className="input_wrapper">
            <input
              type="radio"
              value={false}
              id="use-existing-sub"
              checked={!ownerWantsSubscription}
              onClick={(e) => {
                onOwnerWantsSubscriptionChange(false)
                onPersonUpdate({
                  ...person,
                  useExistingOwner: true,
                  extraAbroad: !ownerWantsSubscription ? person.extraAbroad : false,
                  extraAbroadId: !ownerWantsSubscription ? person.extraAbroadId : null,
                })
              }}
              onChange={(e) => {}}
            />
            <label htmlFor="use-existing-sub">
              <span>
                {mode === 'family' && 'Knytt familie til eierens eksisterende Talkmore-abonnement'}
                {mode === 'single' && 'Knytt bruker til eksisterende Talkmore-abonnement'}
              </span>
            </label>
          </div>
        </div>
      )}
      {!isUkraineFlow && !ownerWantsSubscription && !person.verified && (
        <div>
          <div className="input_wrapper">
            <label htmlFor="ownerPhone">
              Vi sender en SMS til juridisk eier for å godkjenne kjøpet - skriv inn mobilnummeret
              til juridisk eier under:
            </label>
            <input
              type="phone"
              disabled={doingSmsLookup}
              id="ownerPhone"
              name="ownerPhone"
              value={person.ownerPhone || ''}
              onChange={(e) => handleChange(e, true)}
            />
            {doingSmsLookup && (
              <div className="box" style={{ marginTop: '2rem', backgroundColor: '#fff' }}>
                <div>Venter på bekreftelse...</div>
                <button onClick={(e) => initiateSMSVerification()}>Send SMS på nytt</button>
              </div>
            )}
            {!doingSmsLookup && (
              <div className="buttons">
                <button className="search" onClick={() => doSmsPersonLookup()}>
                  Send SMS til juridisk eier for godkjenning
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {!isUkraineFlow && !ownerWantsSubscription && person.verified && (
        <div className="owner-verified-container" style={{ marginBottom: '2rem' }}>
          <i className="fa-icon fas fa-check"></i> Eier har godkjent kjøpet
        </div>
      )}

      {showPersonalia() && (
        <div>
          <div className="input_wrapper">
            <label htmlFor="firstName">Fornavn og mellomnavn:</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={person.firstName || ''}
              onChange={(e) => handleChange(e, !person.useExistingOwner)}
            />
          </div>
          <div className="input_wrapper">
            <label htmlFor="lastName">Etternavn:</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={person.lastName || ''}
              onChange={(e) => handleChange(e, !person.useExistingOwner)}
            />
          </div>
          {!isUkraineFlow && (
            <React.Fragment>
              <div className="input_wrapper">
                <label htmlFor="phone">Telefonnr.:</label>
                <input
                  type="text"
                  id="phone"
                  name="phone"
                  value={person.phone || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="input_wrapper">
                <label htmlFor="email">E-post:</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={person.email || ''}
                  onChange={handleChange}
                />
              </div>
            </React.Fragment>
          )}
          {(isUkraineFlow || (mode === 'single' && formData.singleExistingOwnerPhoneNo)) && (
            <DateOfBirthPicker
              selected={person.dateOfBirth}
              onChange={(value: string) =>
                handleChange({ target: { name: 'dateOfBirth', value: value } })
              }
            />
          )}
          {isUkraineFlow && (
            <React.Fragment>
              <div className="input_wrapper">
                <label htmlFor="email">E-post:</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={person.email || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="input_wrapper">
                <label htmlFor="address">Adresse:</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={person.address || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="input_wrapper">
                <label htmlFor="postalCode">Postnummer:</label>
                <input
                  id="postalCode"
                  type="text"
                  name="postalCode"
                  value={person.postalCode || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="input_wrapper">
                <label htmlFor="postalArea">Poststed:</label>
                <input
                  id="postalArea"
                  type="text"
                  name="postalArea"
                  value={person.postalArea || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="input_wrapper">
                <label htmlFor="simNumber">SIM-nummer:</label>
                <input
                  id="simNumber"
                  type="text"
                  name="simNumber"
                  value={person.simNumber || ''}
                  onChange={handleChange}
                />
              </div>
            </React.Fragment>
          )}

          {!isUkraineFlow &&
            (mode === 'family' || (mode === 'single' && ownerWantsSubscription)) && (
              <div className="input_wrapper">
                <label htmlFor="socialSecurityNumber">Personnr.:</label>
                <input
                  type="text"
                  id="ssn"
                  name="ssn"
                  placeholder="Fyll inn personnummer"
                  value={person.ssn || ''}
                  onChange={(e) => handleChange(e, !person.useExistingOwner)}
                />
              </div>
            )}
        </div>
      )}

      {!isUkraineFlow && (
        <div className="person-lookup-container">
          {!person.verified && config.isDebug && ownerWantsSubscription && (
            <div className="debug_element">
              <div className="buttons">
                <button
                  className="search"
                  disabled={
                    !person.firstName ||
                    !person.lastName ||
                    !person.ssn ||
                    person.ssn.length !== 11 ||
                    person.verified
                  }
                  onClick={() => doFakePersonLookup()}
                >
                  Gjør FAKE oppslag mot Folkeregisteret
                </button>
              </div>
            </div>
          )}

          {!person.verified && config.isDebug && ownerWantsSubscription && (
            <div className="debug_element">
              <div className="buttons">
                <button
                  className="search"
                  disabled={
                    !person.firstName ||
                    !person.lastName ||
                    !person.ssn ||
                    person.ssn.length !== 11 ||
                    person.verified
                  }
                  onClick={() => doPersonLookup(true)}
                >
                  Gjør oppslag mot Folkeregisteret (med feil)
                </button>
              </div>
            </div>
          )}

          {!person.verified && ownerWantsSubscription && (
            <div>
              <div className="buttons">
                <button
                  className="search"
                  disabled={
                    !person.firstName ||
                    !person.lastName ||
                    !person.ssn ||
                    person.ssn.length !== 11 ||
                    person.verified
                  }
                  onClick={() => doPersonLookup()}
                >
                  {lookupMethod === 'dsf' && 'Gjør oppslag mot Folkeregisteret'}
                  {lookupMethod === 'dm' && 'Gjør kredittsjekk'}
                </button>
              </div>
              {lookupMethod === 'dsf' && (
                <div className="switch-lookup-method-container">
                  <span className="link" onClick={() => setLookupMethod('dm')}>
                    Bytt til kredittsjekk (kun hvis folkeregisteret ikke fungerer)
                  </span>
                </div>
              )}
              {lookupMethod === 'dm' && (
                <div className="switch-lookup-method-container">
                  <span className="link" onClick={() => setLookupMethod('dsf')}>
                    Bytt til oppslag mot folkeregisteret
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!person.verified && (person.partlyVerified || person.unableToVerify) && (
        <div className="partly-verified-container">
          <div className="expl">
            {person.partlyVerified &&
              'Personen ble funnet i Folkeregisteret, men det mangler noen felter som må fylles ut før du kan gå videre. Fyll inn de manglende feltene under og trykk Oppdater. Alternativt kan du endre personalia over og gjøre et nytt oppslag mot Folkeregisteret.'}
            {person.unableToVerify &&
              'Vi var ikke i stand til å sjekke personen mot Folkeregisteret, sannsynligvis pga. en feil hos de. Vennligst fyll ut de feltene som mangler og trykk Oppdater.'}
          </div>
          {!person.address && (
            <div className="input_wrapper">
              <label htmlFor="missing_address">Adresse:</label>
              <input
                id="missing_address"
                type="text"
                value={missingAddress}
                onChange={(ev) => setMissingAddress(ev.target.value)}
              />
            </div>
          )}
          {!person.postalCode && (
            <div className="input_wrapper">
              <label htmlFor="missing_postal_code">Postnummer:</label>
              <input
                id="missing_postal_code"
                type="text"
                value={missingPostalCode}
                onChange={(ev) => setMissingPostalCode(ev.target.value)}
              />
            </div>
          )}
          {!person.postalArea && (
            <div className="input_wrapper">
              <label htmlFor="missing_postal_area">Poststed:</label>
              <input
                id="missing_postal_area"
                type="text"
                value={missingPostalArea}
                onChange={(ev) => setMissingPostalArea(ev.target.value)}
              />
            </div>
          )}
          <div className="buttons">
            <button
              disabled={
                !(
                  (person.address || missingAddress) &&
                  (person.postalCode || missingPostalCode) &&
                  (person.postalArea || missingPostalArea)
                )
              }
              onClick={() => updatePersonData()}
            >
              Oppdater
            </button>
          </div>
        </div>
      )}

      {mode === 'family' && person.verified && ownerWantsSubscription && (
        <React.Fragment>
          <h3>Tilleggstjenester for eier/familie</h3>
          {filteredFamilyExtraServices.map((svc: any) => {
            const extraServices = person.extraServices || []
            const existingIdx = extraServices.findIndex((s: any) => s === svc.id)
            return (
              <div key={svc.id}>
                <div className="input_wrapper">
                  <input
                    type="checkbox"
                    value={1}
                    id={`owner_${svc.id}`}
                    checked={existingIdx !== -1}
                    onChange={(e) => {
                      const newExtraServices = e.target.checked
                        ? existingIdx === -1
                          ? extraServices.concat([svc.id])
                          : extraServices
                        : existingIdx === -1
                          ? extraServices
                          : extraServices
                              .slice(0, existingIdx)
                              .concat(extraServices.slice(existingIdx + 1))
                      onPersonUpdate({
                        ...person,
                        extraServices: newExtraServices,
                      })
                    }}
                  />
                  <label htmlFor={`owner_${svc.id}`}>
                    <span>{svc.name}</span>
                  </label>
                </div>
              </div>
            )
          })}
        </React.Fragment>
      )}

      {person.verified && Boolean(ownerWantsSubscription && !hidePortType) && (
        <React.Fragment>
          <h3>Nytt nummer eller portering</h3>
          <PortType person={person} onPersonChange={onPersonUpdate} />
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default OwnerInputComponent
